import { z } from "zod";
import type { BotRule, Totals } from "./bot-funnel";
import type { UrlActiveFilters } from "./url-active-filters";

/**
 * Bot presets on Clicks: which bot rules hide clicks from the report.
 *
 * - "All traffic": nothing hidden - no is_own_bot filter at all (the default).
 * - "Global rules": hidden by the rules switched on in Settings → Bot Detection.
 * - A saved preset or a custom pick: hidden by exactly those rules, whatever
 *   Settings says - it overrides the global rules for this report.
 *
 * The rules travel in the URL filter itself, {"is_own_bot": {"mode":
 * "exclude", "values": ["true"], "rules": [...]}}, so a link or a saved view
 * reproduces the numbers even where the preset was never saved; the Bots
 * columns follow the same rules (migration 20261021130000). Presets are kept
 * per browser, like saved views.
 */

export const BOT_FILTER_KEY = "is_own_bot";
export const BOT_PRESETS_KEY = "oracul.clicks.botPresets.v1";

export interface BotPreset {
  id: string;
  name: string;
  /** bot_rules keys, e.g. ["user_agent", "datacenter_isp"]. */
  rules: string[];
}

export type BotSelection = { kind: "all" } | { kind: "global" } | { kind: "rules"; rules: string[] };

const sorted = (rules: string[]) => [...new Set(rules)].sort();
const sameRules = (a: string[], b: string[]) => sorted(a).join("\u0000") === sorted(b).join("\u0000");

/** What the URL's filters hide: nothing, the global rules, or a set of rules. */
export const botSelectionOf = (filters: UrlActiveFilters): BotSelection => {
  const entry = filters[BOT_FILTER_KEY];
  if (!entry || !("values" in entry) || entry.mode !== "exclude" || !entry.values.includes("true")) return { kind: "all" };
  if (!entry.rules) return { kind: "global" };
  return entry.rules.length ? { kind: "rules", rules: sorted(entry.rules) } : { kind: "all" };
};

/** The filters with this selection in place of whatever bot filter they had. */
export const withBotSelection = (filters: UrlActiveFilters, selection: BotSelection): UrlActiveFilters => {
  const next = { ...filters };
  delete next[BOT_FILTER_KEY];
  if (selection.kind === "global") next[BOT_FILTER_KEY] = { mode: "exclude", values: ["true"] };
  if (selection.kind === "rules" && selection.rules.length)
    next[BOT_FILTER_KEY] = { mode: "exclude", values: ["true"], rules: sorted(selection.rules) };
  return next;
};

/** The saved preset with exactly these rules, if any. */
export const matchPreset = (rules: string[], presets: BotPreset[]): BotPreset | undefined =>
  presets.find((p) => sameRules(p.rules, rules));

/** "All traffic" / "Global rules" / a preset's name / "Custom: User agents + ISP" / "Custom: 5 rules". */
export const describeBotSelection = (
  selection: BotSelection,
  presets: BotPreset[],
  ruleLabel: (key: string) => string = (k) => k,
): string => {
  if (selection.kind === "all") return "All traffic";
  if (selection.kind === "global") return "Global rules";
  const preset = matchPreset(selection.rules, presets);
  if (preset) return preset.name;
  // Three or more names do not fit a toolbar button.
  return selection.rules.length > 2
    ? `Custom: ${selection.rules.length} rules`
    : `Custom: ${selection.rules.map(ruleLabel).join(" + ")}`;
};

/** The rules as a selection switches them: none, the saved global ones, or exactly the preset's. */
export const rulesForSelection = (rules: BotRule[], selection: BotSelection): BotRule[] =>
  selection.kind === "global"
    ? rules
    : rules.map((r) => ({ ...r, enabled: selection.kind === "rules" && selection.rules.includes(r.key) }));

export const sameSelection = (a: BotSelection, b: BotSelection): boolean =>
  a.kind === b.kind && (a.kind !== "rules" || (b.kind === "rules" && sameRules(a.rules, b.rules)));

const plural = (n: number, word: string) => `${n} ${word}${n === 1 ? "" : "s"}`;

export interface PresetVerdict {
  tone: "ok" | "muted" | "warn";
  text: string;
}

/**
 * The line under a preset, by the SEO agent's rule (22.10.2026). "Hides real
 * money" only when it hides 2+ sales, 2%+ of the revenue and the hidden clicks
 * register at least 0.2x as often as the kept ones; any other hidden
 * registration or sale is said plainly, in grey. "Hides no conversions" only
 * when the hidden clicks, converting like the kept ones, would have brought 3+
 * registrations - with fewer expected, none hidden proves nothing.
 */
export const presetVerdict = (total: Totals, clean: Totals): PresetVerdict => {
  const hidden = { clicks: total.clicks - clean.clicks, regs: total.conversions - clean.conversions,
    sales: total.sales - clean.sales, revenue: total.revenue - clean.revenue };
  const keptRate = clean.clicks > 0 ? clean.conversions / clean.clicks : 0;
  const hiddenRate = hidden.clicks > 0 ? hidden.regs / hidden.clicks : 0;
  if (hidden.sales >= 2 && total.revenue > 0 && hidden.revenue / total.revenue >= 0.02 && keptRate > 0 && hiddenRate / keptRate >= 0.2)
    return { tone: "warn", text: `hides real money: ${plural(hidden.sales, "sale")}` };
  if (hidden.regs > 0 || hidden.sales > 0)
    return { tone: "muted", text: `hides ${plural(hidden.regs, "reg")}${hidden.sales ? ` · ${plural(hidden.sales, "sale")}` : ""}` };
  if (!hidden.clicks) return { tone: "muted", text: "nothing hidden" };
  return hidden.clicks * keptRate >= 3
    ? { tone: "ok", text: "hides no conversions" }
    : { tone: "muted", text: "too few clicks to judge" };
};

const sameName = (a: string, b: string) => a.trim().toLowerCase() === b.trim().toLowerCase();

/** Upsert by name, as saved views do: saving under an existing name overwrites it. */
export const addPreset = (list: BotPreset[], preset: BotPreset): BotPreset[] => {
  const clean = { ...preset, name: preset.name.trim(), rules: sorted(preset.rules) };
  const index = list.findIndex((p) => sameName(p.name, clean.name));
  if (index === -1) return [...list, clean];
  const next = [...list];
  next[index] = { ...clean, id: list[index].id };
  return next;
};

export const removePreset = (list: BotPreset[], id: string): BotPreset[] => list.filter((p) => p.id !== id);

const presetSchema = z.object({ id: z.string(), name: z.string(), rules: z.array(z.string()) });

/** Safe parse of the stored JSON; bad entries are dropped, junk input → []. */
export const parsePresets = (raw: string | null): BotPreset[] => {
  if (!raw) return [];
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return [];
  }
  if (!Array.isArray(parsed)) return [];
  return parsed.flatMap((item) => {
    const r = presetSchema.safeParse(item);
    return r.success ? [r.data] : [];
  });
};

export const loadPresets = (): BotPreset[] => {
  try {
    return parsePresets(localStorage.getItem(BOT_PRESETS_KEY));
  } catch {
    return [];
  }
};

export const persistPresets = (list: BotPreset[]): BotPreset[] => {
  try {
    localStorage.setItem(BOT_PRESETS_KEY, JSON.stringify(list));
  } catch {
    // Private mode / disabled storage - the list still works for this session.
  }
  return list;
};
